// Journal landing page (/blog): topic pills + archive-wide search + load more.
// Browsing filters editorial (type=Blog) stories by topic; a typed search widens
// to the full archive (blog + newsroom) per the redesign handoff.
import { initRoutes, stardust } from '@eidellev/adonis-stardust/client';
initRoutes();

import { getPageLang } from './modules/utils';

const grid = document.querySelector<HTMLElement>('.js-journal-grid');

if (grid) {
  const topics = Array.from(document.querySelectorAll<HTMLButtonElement>('.js-topic'));
  const searchInput = document.querySelector<HTMLInputElement>('.js-journal-search');
  const clearBtn = document.querySelector<HTMLButtonElement>('.js-journal-clear');
  const status = document.querySelector<HTMLElement>('.js-results-status');
  const heading = document.querySelector<HTMLElement>('.js-results-heading');
  const moreBtn = document.querySelector<HTMLButtonElement>('.js-journal-more');
  const emptyState = document.querySelector<HTMLElement>('.js-journal-empty');
  const resetBtn = document.querySelector<HTMLButtonElement>('.js-journal-reset');

  let topic = 'all';
  let query = '';
  let page = 1;
  let total = parseInt(grid.dataset.total || '0', 10);
  let shown = parseInt(grid.dataset.shown || '0', 10);
  let working = false;
  let debounceTimer: number | undefined;

  function updateStatus() {
    if (!status) return;
    const template = grid!.dataset.countTemplate || '%shown% / %total%';
    status.textContent = template
      .replace('%shown%', String(Math.min(shown, total)))
      .replace('%total%', String(total));

    if (heading) {
      heading.textContent = query
        ? grid!.dataset.searchHeading || heading.textContent || ''
        : grid!.dataset.journalHeading || heading.textContent || '';
    }
  }

  async function load(nextPage: number, { append = false } = {}) {
    if (working) return;
    working = true;
    moreBtn?.setAttribute('disabled', '');

    const qs: Record<string, any> = { page: nextPage, lang: getPageLang() };

    if (query) {
      // Search spans the whole archive (blog + newsroom)
      qs.q = query;
    } else {
      qs.type = 'Blog';
    }

    if (topic !== 'all') {
      qs.topic = topic;
    }

    const fetchUrl = stardust.route('api.blog.paginate', undefined, { qs });

    try {
      const { html, items } = await fetch(fetchUrl).then((r) => r.json());

      if (!append) {
        grid!.innerHTML = '';
        shown = 0;
      }

      const fragment = document.createDocumentFragment();
      const wrapper = document.createElement('div');

      (html || []).forEach((cardHtml: string) => {
        wrapper.innerHTML = cardHtml;
        while (wrapper.firstElementChild) {
          fragment.appendChild(wrapper.firstElementChild);
        }
      });

      const firstNew = fragment.querySelector<HTMLElement>('.story-card__link');
      grid!.appendChild(fragment);

      page = nextPage;
      total = items?.total ?? 0;
      shown += (html || []).length;

      const hasResults = shown > 0;
      emptyState?.toggleAttribute('hidden', hasResults);

      if (moreBtn) {
        moreBtn.toggleAttribute('hidden', shown >= total);
        moreBtn.removeAttribute('disabled');
      }

      updateStatus();

      if (append && firstNew) {
        firstNew.focus();
      }
    } finally {
      working = false;
      moreBtn?.removeAttribute('disabled');
    }
  }

  function setTopic(next: string) {
    topic = next;
    topics.forEach((btn) => {
      btn.setAttribute('aria-pressed', btn.dataset.topic === next ? 'true' : 'false');
    });
    load(1);
  }

  topics.forEach((btn) => {
    btn.addEventListener('click', () => {
      if (btn.dataset.topic !== topic) setTopic(btn.dataset.topic || 'all');
    });
  });

  searchInput?.addEventListener('input', () => {
    const value = searchInput.value.trim();
    clearBtn?.toggleAttribute('hidden', !value);

    window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(() => {
      if (value === query) return;

      const hadQuery = Boolean(query);
      query = value;

      // Starting a search resets the topic so the archive-wide scope is accurate
      if (query && !hadQuery && topic !== 'all') {
        topic = 'all';
        topics.forEach((btn) => {
          btn.setAttribute('aria-pressed', btn.dataset.topic === 'all' ? 'true' : 'false');
        });
      }

      load(1);
    }, 350);
  });

  function clearAll() {
    if (searchInput) searchInput.value = '';
    clearBtn?.setAttribute('hidden', '');
    query = '';
    setTopic('all');
  }

  clearBtn?.addEventListener('click', () => {
    clearAll();
    searchInput?.focus();
  });

  resetBtn?.addEventListener('click', clearAll);

  moreBtn?.addEventListener('click', () => load(page + 1, { append: true }));

  updateStatus();
}
